import React, { Suspense, lazy, useCallback, useRef, useState, type RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { MdInsertDriveFile, MdDownload, MdContentCopy, MdCheck, Md3dRotation, MdExpandMore, MdOpenInFull, MdPictureAsPdf, MdHtml } from 'react-icons/md';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { ModelViewer, ModelViewerModal, modelOf, type ModelLoaderType } from '@aibuild/model-viewer';
import {
  ArtifactChip,
  ArtifactCardContainer,
  ArtifactCardIcon,
  ArtifactCardBody,
  ArtifactFileName,
  ArtifactDescription,
  ArtifactMeta,
  ArtifactLocalPath,
  ArtifactDownloadButton,
  ArtifactCollapseButton,
  ArtifactPreviewCollapse,
  ArtifactImageWrapper,
  ArtifactImageSkeleton,
  ArtifactImagePreview,
  ModelPreviewContainer,
  ModelPreviewExpandButton,
  ModelPreviewFallback,
  ModelPreviewRetryButton,
  ModelViewerSkeleton,
  LoadModelButton,
  PdfPreviewContainer,
} from './Chat.styled';
import { formatFileSize } from './helpers';

import useAuthImage from '@hooks/useAuthImage';
import { useModelBlob } from '@hooks/useModelBlob';
import type { ArtifactInfo } from '@hooks/useChat';
import { isArtifactDownloadUrl } from '@utils/authDownload';
import useAuthDownload from '@hooks/useAuthDownload';
import useInViewport from '@hooks/useInViewport';
import { RenderErrorBoundary } from '@components/ErrorBoundary';
import { canPreviewPdf } from '@components/PdfViewer/canPreviewPdf';
import { useCanPreviewHtml } from '@components/HtmlArtifact';
import { classifyAttachment } from '@components/Attachments/attachmentKind';
import { IMAGE_THUMBNAIL_MAX_BYTES } from '@components/Attachments/AttachmentTray.constants';

const PdfPreview = lazy(() => import('@components/PdfViewer/PdfPreview'));
const HtmlArtifactPreview = lazy(() => import('@components/HtmlArtifact/HtmlArtifactPreview'));

const MODEL_AUTOLOAD_LIMIT = 20 * 1024 * 1024; // 20 MB
const MODEL_MAX_SIZE = 100 * 1024 * 1024; // 100 MB

// Local runners send their own `imageUrl`; an uploaded image is shown from its download URL.
const inlineImageUrl = (info: ArtifactInfo): string | undefined => {
  if (info.imageUrl || info.localPath || !info.downloadUrl) return info.imageUrl;
  const previewable = classifyAttachment('', info.name) === 'image'
    && info.fileSize > 0
    && info.fileSize <= IMAGE_THUMBNAIL_MAX_BYTES;
  return previewable ? info.downloadUrl : undefined;
};

const previewableUrl = (
  info: ArtifactInfo,
  isLocal: boolean,
  canPreview: (name: string, fileSize: number, contentType?: string) => boolean,
): string | undefined =>
  !isLocal && info.downloadUrl && canPreview(info.name, info.fileSize, info.contentType)
    ? info.downloadUrl
    : undefined;

const CardIcon: React.FC<{ show3DViewer: boolean; isPdf: boolean; isHtml: boolean }> = ({ show3DViewer, isPdf, isHtml }) => {
  if (show3DViewer) return <Md3dRotation size={22} />;
  if (isPdf) return <MdPictureAsPdf size={22} />;
  if (isHtml) return <MdHtml size={22} />;
  return <MdInsertDriveFile size={22} />;
};

interface ModelPreviewSectionProps {
  blob: Blob | null;
  downloadFailed: boolean;
  onRetryDownload: () => void;
  info: ArtifactInfo;
  loaderType: ModelLoaderType;
  modelBytes: number;
  loadModelRequested: boolean;
  modelAutoLoad: boolean;
  modelModalOpen: boolean;
  onRequestLoad: () => void;
  onExpandClick: () => void;
  onModalClose: () => void;
}

// Shares the viewer's own message ids so the chat card and the viewer overlay
// never disagree about the same two words.
const PreviewUnavailable: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ModelPreviewFallback data-testid="model-preview-error">
    <span>
      <FormattedMessage id="modelViewer.unavailable" defaultMessage="3D view unavailable" />
    </span>
    {onRetry && (
      <ModelPreviewRetryButton type="button" onClick={onRetry}>
        <FormattedMessage id="modelViewer.retry" defaultMessage="Retry" />
      </ModelPreviewRetryButton>
    )}
  </ModelPreviewFallback>
);

const ModelPreviewSection: React.FC<ModelPreviewSectionProps> = ({
  blob, downloadFailed, onRetryDownload, info, loaderType, modelBytes, loadModelRequested,
  modelAutoLoad, modelModalOpen, onRequestLoad, onExpandClick, onModalClose,
}) => {
  const showLoader = !blob && !downloadFailed && !loadModelRequested && !modelAutoLoad;

  // While auto-loading, show a skeleton inside ModelPreviewContainer so the
  // space is reserved at the correct aspect-ratio size before the blob arrives.
  // This prevents layout shifts that break auto-scroll.
  if (showLoader) {
    return (
      <LoadModelButton type="button" onClick={onRequestLoad}>
        <Md3dRotation size={16} />
        Load 3D preview ({formatFileSize(modelBytes)})
      </LoadModelButton>
    );
  }

  // A failed download is terminal until the user asks again — showing the
  // skeleton forever (the old behaviour) reads as a hung preview.
  if (downloadFailed) {
    return (
      <ModelPreviewContainer>
        <PreviewUnavailable onRetry={onRetryDownload} />
      </ModelPreviewContainer>
    );
  }

  return (
    <>
      <ModelPreviewContainer>
        {blob ? (
          <RenderErrorBoundary
            label="ModelViewer"
            fileName={info.name}
            fallback={<PreviewUnavailable />}
          >
            <ModelViewer data={blob} fileName={info.name} loaderType={loaderType} compact />
            <ModelPreviewExpandButton
              type="button"
              title="Expand"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onExpandClick(); }}
            >
              <MdOpenInFull size={15} />
            </ModelPreviewExpandButton>
          </RenderErrorBoundary>
        ) : (
          <ModelViewerSkeleton data-testid="model-skeleton" />
        )}
      </ModelPreviewContainer>
      {modelModalOpen && blob && (
        <ModelViewerModal
          data={blob}
          fileName={info.name}
          loaderType={loaderType}
          onClose={onModalClose}
        />
      )}
    </>
  );
};

interface ArtifactModelState {
  cardRef: RefObject<HTMLDivElement | null>;
  show3DViewer: boolean;
  loaderType: ModelLoaderType | null;
  modelBytes: number;
  modelTooLarge: boolean;
  modelAutoLoad: boolean;
  modelBlob: Blob | null;
  modelDownloadFailed: boolean;
  retryModelDownload: () => void;
  loadModelRequested: boolean;
  requestLoadModel: () => void;
}

function useArtifactModel(info: ArtifactInfo, isLocal: boolean): ArtifactModelState {
  const cardRef = useRef<HTMLDivElement>(null);
  const isInViewport = useInViewport(cardRef);
  const [loadModelRequested, setLoadModelRequested] = useState(false);

  // previewUrl is the derived preview (cloud CAD) or a file-server URL (local
  // mode, where the file itself stays on the runner's disk).
  const model = modelOf({
    name: info.name,
    source: !isLocal && info.downloadUrl ? info.downloadUrl : null,
    preview: info.previewUrl ? { source: info.previewUrl, name: info.previewName } : null,
  });
  const show3DViewer = model !== null;
  const viewerUrl = model?.source;
  const loaderType = model?.loaderType ?? null;

  const modelBytes = model?.from === 'preview' ? info.previewFileSize ?? info.fileSize : info.fileSize;
  const modelTooLarge = modelBytes > MODEL_MAX_SIZE;
  const modelAutoLoad = modelBytes <= MODEL_AUTOLOAD_LIMIT;
  const canFetch = show3DViewer && loaderType !== null && !modelTooLarge && isInViewport;
  const shouldFetchModel = canFetch && (modelAutoLoad || loadModelRequested);

  // Local file-server URLs are cross-origin; the authed API client rejects
  // them, so they are fetched directly instead.
  const isLocalFileServerUrl = !!viewerUrl && !isArtifactDownloadUrl(viewerUrl);
  const { blob, failed, retry } = useModelBlob(
    viewerUrl,
    shouldFetchModel,
    !isLocalFileServerUrl,
  );

  return {
    cardRef,
    show3DViewer,
    loaderType,
    modelBytes,
    modelTooLarge,
    modelAutoLoad,
    modelBlob: blob,
    modelDownloadFailed: failed,
    retryModelDownload: retry,
    loadModelRequested,
    requestLoadModel: () => setLoadModelRequested(true),
  };
}

interface ImagePreviewProps {
  info: ArtifactInfo;
  resolvedImageUrl: string | null | undefined;
  authImageError: boolean;
  isArtifactUrl: boolean;
}

const ImagePreviewSection: React.FC<ImagePreviewProps> = ({
  info, resolvedImageUrl, authImageError, isArtifactUrl,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const showSection = resolvedImageUrl || (isArtifactUrl && !authImageError);
  if (!showSection) return null;

  return (
    <>
      <ArtifactImageWrapper>
        {(!resolvedImageUrl || !imageLoaded) && !authImageError && (
          <ArtifactImageSkeleton data-testid="image-skeleton" />
        )}
        {resolvedImageUrl && (
          <ArtifactImagePreview
            src={resolvedImageUrl}
            alt={info.name}
            $loaded={imageLoaded}
            onLoad={() => setImageLoaded(true)}
            onClick={() => setLightboxOpen(true)}
          />
        )}
      </ArtifactImageWrapper>
      {resolvedImageUrl && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: resolvedImageUrl }]}
          plugins={[Zoom]}
          carousel={{ finite: true }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}
    </>
  );
};

const ArtifactCard: React.FC<{ info: ArtifactInfo }> = ({ info }) => {
  const isLocal = !!info.localPath;
  const canPreviewHtml = useCanPreviewHtml();
  const { download, downloading } = useAuthDownload();
  const [copied, setCopied] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(true);

  const {
    cardRef, show3DViewer, loaderType, modelBytes, modelTooLarge, modelAutoLoad, modelBlob,
    modelDownloadFailed, retryModelDownload, loadModelRequested, requestLoadModel,
  } = useArtifactModel(info, isLocal);

  const imageUrl = inlineImageUrl(info);
  const isArtifactUrl = imageUrl ? isArtifactDownloadUrl(imageUrl) : false;
  const { url: authBlobUrl, hasError: authImageError } = useAuthImage(isArtifactUrl ? imageUrl : undefined);
  const resolvedImageUrl = isArtifactUrl ? authBlobUrl : imageUrl;

  const handleDownload = useCallback(() => {
    if (info.downloadUrl) void download(info.downloadUrl, info.name);
  }, [download, info.downloadUrl, info.name]);

  const handleCopyPath = useCallback(async () => {
    if (!info.localPath) return;
    try {
      await navigator.clipboard.writeText(info.localPath);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy path:', err);
    }
  }, [info.localPath]);

  const pdfUrl = previewableUrl(info, isLocal, canPreviewPdf);
  const htmlUrl = previewableUrl(info, isLocal, canPreviewHtml);
  const hasPreview = show3DViewer || !!resolvedImageUrl || !!pdfUrl || !!htmlUrl;

  return (
    <ArtifactChip ref={cardRef}>
      <ArtifactCardContainer>
        <ArtifactCardIcon>
          <CardIcon show3DViewer={show3DViewer} isPdf={!!pdfUrl} isHtml={!!htmlUrl} />
        </ArtifactCardIcon>
        <ArtifactCardBody>
          <ArtifactFileName>{info.name}</ArtifactFileName>
          {info.description && (
            <ArtifactDescription title={info.description}>{info.description}</ArtifactDescription>
          )}
          {info.fileSize > 0 && <ArtifactMeta>{formatFileSize(info.fileSize)}</ArtifactMeta>}
          {isLocal && !info.imageUrl && (
            <ArtifactLocalPath title={info.localPath}>{info.localPath}</ArtifactLocalPath>
          )}
        </ArtifactCardBody>
        {isLocal ? (
          <ArtifactDownloadButton
            as="button"
            onClick={handleCopyPath}
            title={copied ? 'Copied!' : 'Copy path'}
          >
            {copied ? <MdCheck size={18} /> : <MdContentCopy size={18} />}
          </ArtifactDownloadButton>
        ) : (
          <ArtifactDownloadButton
            as="button"
            onClick={handleDownload}
            title="Download"
            $downloading={downloading}
          >
            <MdDownload size={18} />
          </ArtifactDownloadButton>
        )}
        {hasPreview && (
          <ArtifactCollapseButton
            type="button"
            $expanded={previewExpanded}
            onClick={() => setPreviewExpanded((prev) => !prev)}
            title={previewExpanded ? 'Collapse preview' : 'Expand preview'}
          >
            <MdExpandMore size={20} />
          </ArtifactCollapseButton>
        )}
      </ArtifactCardContainer>

      <ArtifactPreviewCollapse $expanded={previewExpanded}>
        {show3DViewer && loaderType && !modelTooLarge && (
          <ModelPreviewSection
            blob={modelBlob}
            downloadFailed={modelDownloadFailed}
            onRetryDownload={retryModelDownload}
            info={info}
            loaderType={loaderType}
            modelBytes={modelBytes}
            loadModelRequested={loadModelRequested}
            modelAutoLoad={modelAutoLoad}
            modelModalOpen={modelModalOpen}
            onRequestLoad={requestLoadModel}
            onExpandClick={() => setModelModalOpen(true)}
            onModalClose={() => setModelModalOpen(false)}
          />
        )}
        {pdfUrl && (
          <PdfPreviewContainer>
            <Suspense fallback={null}>
              <PdfPreview url={pdfUrl} name={info.name} fileSize={info.fileSize} />
            </Suspense>
          </PdfPreviewContainer>
        )}
        {htmlUrl && (
          <Suspense fallback={null}>
            <HtmlArtifactPreview url={htmlUrl} name={info.name} />
          </Suspense>
        )}
        <ImagePreviewSection
          key={resolvedImageUrl ?? ''}
          info={info}
          resolvedImageUrl={resolvedImageUrl}
          authImageError={authImageError}
          isArtifactUrl={isArtifactUrl}
        />
      </ArtifactPreviewCollapse>
    </ArtifactChip>
  );
};

export default ArtifactCard;
